/**
 * The phone's location, fed into the session.
 *
 * Geolocation needs the same secure context Web Bluetooth does, so wherever
 * the adapter can connect, this can run. Permission is only ever asked for in
 * response to the driver turning the feature on: a prompt that appears on its
 * own at startup is one people learn to dismiss, and some browsers quietly
 * block it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ObdSession } from '@obd/core';

export type LocationPermission = 'unsupported' | 'unknown' | 'prompt' | 'granted' | 'denied';

export interface GpsStatus {
  permission: LocationPermission;
  /** A position watch is running. */
  watching: boolean;
  /** Something worth telling the driver, e.g. why there is no fix. */
  problem: string | null;
}

export function geolocationAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator && !!navigator.geolocation;
}

/** GeolocationPositionError codes, spelled out: polyfills do not all define the constants. */
const PERMISSION_DENIED = 1;
const TIMEOUT = 3;

const DENIED_HELP =
  'Location is blocked for this site. Allow it in the browser’s site settings (the icon left of the address), then turn this on again.';

/**
 * Ask for location permission. Call it straight from a tap: the prompt it
 * raises is tied to that gesture. Resolves once the driver has answered.
 */
export function requestLocationPermission(): Promise<'granted' | 'denied' | 'unavailable'> {
  return new Promise((resolve) => {
    if (!geolocationAvailable()) {
      resolve('unavailable');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => resolve('granted'),
      // Only a refusal is a refusal; a timeout just means no fix yet.
      (err) => resolve(err.code === PERMISSION_DENIED ? 'denied' : 'granted'),
      { enableHighAccuracy: true, maximumAge: 60_000, timeout: 20_000 },
    );
  });
}

/** Track the browser's own record of the permission, where it will say. */
function usePermissionState(): LocationPermission {
  const [state, setState] = useState<LocationPermission>(geolocationAvailable() ? 'unknown' : 'unsupported');

  useEffect(() => {
    if (!geolocationAvailable() || !navigator.permissions?.query) return;
    let status: PermissionStatus | undefined;
    let cancelled = false;
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((s) => {
        if (cancelled) return;
        status = s;
        setState(s.state);
        s.onchange = () => setState(s.state);
      })
      .catch(() => {
        /* some browsers do not answer for geolocation */
      });
    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, []);

  return state;
}

/**
 * Stream position fixes into the session while `enabled`.
 *
 * High accuracy is requested because speed and distance are the whole point:
 * a network-derived position has no usable speed. The watch is restarted if
 * permission comes back after having been denied.
 */
export function useGpsFeed(session: ObdSession, enabled: boolean): GpsStatus {
  const permission = usePermissionState();
  const [watching, setWatching] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const deniedNow = denied || permission === 'denied';
  const problemRef = useRef(problem);
  problemRef.current = problem;

  // A permission granted from the site settings clears an earlier refusal, and
  // so does switching the feature off: switching it back on is a fresh try.
  useEffect(() => {
    if (permission === 'granted' || !enabled) setDenied(false);
  }, [permission, enabled]);

  useEffect(() => {
    if (!enabled || !geolocationAvailable() || deniedNow) {
      setWatching(false);
      if (!enabled) setProblem(null);
      else if (deniedNow) setProblem(DENIED_HELP);
      return;
    }

    const id = navigator.geolocation.watchPosition(
      (pos) => {
        session.ingestGps({
          t: pos.timestamp,
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracyM: pos.coords.accuracy,
          speedMps: pos.coords.speed,
        });
        if (problemRef.current !== null) setProblem(null);
      },
      (err) => {
        if (err.code === PERMISSION_DENIED) {
          setDenied(true);
        } else if (err.code === TIMEOUT) {
          setProblem('Waiting for a GPS fix. Indoors and in car parks this can take a while.');
        } else {
          setProblem('The phone could not determine its position' + (err.message ? ': ' + err.message : '.'));
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 30_000 },
    );
    setWatching(true);
    return () => {
      navigator.geolocation.clearWatch(id);
      setWatching(false);
    };
  }, [enabled, deniedNow, session]);

  const reported: LocationPermission = deniedNow ? 'denied' : permission;
  return useMemo(() => ({ permission: reported, watching, problem }), [reported, watching, problem]);
}
